import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Calendar, CreditCard, User, LogOut, MapPin, Sparkles, ArrowRight, QrCode, ArrowLeft, Home, ExternalLink, Map as MapIcon, LayoutGrid } from "lucide-react";
import SessionsMap from "../components/SessionsMap";

function fmt(d) { return new Date(d).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); }

function Browse() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list"); // "list" | "map"
  const load = () => api.get("/sessions").then(r => setSessions(r.data));
  useEffect(() => { load(); }, []);

  const register = async (id) => {
    try {
      const { data } = await api.post(`/sessions/${id}/register`);
      if (data.used_subscription) toast.success("Inscription confirmée — 1 séance utilisée sur votre carnet.");
      else toast.success("Inscription enregistrée. Vous pouvez régler la séance depuis 'Mon planning'.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? sessions : sessions.filter(s => s.discipline === filter);
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Prochaines séances</h1>
        <div className="flex gap-1 p-1 rounded-full bg-white border border-slate-200" data-testid="browse-view-toggle">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "list" ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)]"}`} data-testid="browse-view-list">
            <LayoutGrid size={14}/> Liste
          </button>
          <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "map" ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)]"}`} data-testid="browse-view-map">
            <MapIcon size={14}/> Carte
          </button>
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {[["all", "Toutes"], ["nordic_yoga", "Nordic Yoga"], ["marche_nordique", "Marche"], ["stage", "Stages"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-4 py-1.5 rounded-full text-sm ${filter === k ? "bg-[var(--pine)] text-white" : "bg-white text-[var(--muted-text)] border border-slate-200"}`}
            data-testid={`filter-discipline-${k}`}>{l}</button>
        ))}
      </div>
      {view === "map" ? (
        <div className="mt-8">
          <SessionsMap sessions={filtered} height="560px" onRegister={register} />
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map(s => (
            <div key={s.id} className="card-warm p-6" data-testid={`browse-session-${s.id}`}>
              <span className={`badge-discipline ${s.discipline === "nordic_yoga" ? "badge-yoga" : s.discipline === "marche_nordique" ? "badge-marche" : "badge-stage"}`}>
                {s.discipline === "nordic_yoga" ? "Nordic Yoga" : s.discipline === "marche_nordique" ? "Marche" : "Stage"}
              </span>
              <h3 className="mt-3 font-serif-display text-xl font-semibold" style={{ color: "var(--pine)" }}>{s.title}</h3>
              <div className="mt-3 text-sm text-[var(--muted-text)] flex items-center gap-2"><Calendar size={14}/>{fmt(s.start_at)}</div>
              <div className="text-sm text-[var(--muted-text)] flex items-center gap-2"><MapPin size={14}/>{s.location}</div>
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold" style={{ color: "var(--terracotta)" }}>{s.price_eur} €</div>
                  <div className="text-xs text-[var(--muted-text)]">{s.places_left} places restantes</div>
                </div>
                <button onClick={() => register(s.id)} disabled={s.places_left === 0} className="btn-primary text-sm disabled:opacity-40" data-testid={`session-card-register-button-${s.id}`}>
                  {s.places_left === 0 ? "Complet" : "Réserver"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyPlanning() {
  const [bookings, setBookings] = useState([]);
  const [qrOpenId, setQrOpenId] = useState(null);
  const load = () => api.get("/my/bookings").then(r => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async (sid) => {
    if (!window.confirm("Annuler votre inscription ?")) return;
    await api.delete(`/sessions/${sid}/register`);
    toast.success("Inscription annulée");
    load();
  };

  const upcoming = bookings.filter(b => !b.cancelled && new Date(b.session.start_at) >= new Date());
  const past = bookings.filter(b => b.cancelled || new Date(b.session.start_at) < new Date());

  return (
    <div>
      <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Mon planning</h1>
      <h2 className="mt-6 overline">À venir</h2>
      <div className="mt-3 space-y-3">
        {upcoming.length === 0 && <div className="text-[var(--muted-text)]">Aucune séance à venir.</div>}
        {upcoming.map(b => (
          <div key={b.id} className="card-warm p-5" data-testid={`my-booking-${b.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-semibold" style={{ color: "var(--pine)" }}>{b.session.title}</div>
                <div className="text-sm text-[var(--muted-text)]">{fmt(b.session.start_at)} · {b.session.location}</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {b.paid ? <span className="badge-discipline badge-yoga">Payé ({b.payment_method})</span> :
                  <span className="badge-discipline badge-marche" data-testid={`booking-tobepaid-${b.id}`}>À régler sur place · {b.session.price_eur}€ (espèces / QR code / virement)</span>}
                <button onClick={() => setQrOpenId(qrOpenId === b.id ? null : b.id)} className="btn-ghost text-xs border" data-testid={`booking-qr-${b.id}`}>
                  <QrCode size={14}/> {qrOpenId === b.id ? "Fermer" : "Mon badge"}
                </button>
                <button onClick={() => cancel(b.session.id)} className="text-xs text-red-600 hover:underline" data-testid={`booking-cancel-${b.id}`}>Annuler</button>
              </div>
            </div>
            {qrOpenId === b.id && b.qr_token && (
              <div className="mt-4 pt-4 border-t flex flex-col items-center" data-testid={`booking-qr-panel-${b.id}`}>
                <div className="p-4 bg-white border rounded-xl">
                  <QRCodeSVG value={b.qr_token} size={200} level="M" />
                </div>
                <div className="mt-3 text-xs text-[var(--muted-text)] text-center max-w-xs">
                  Présentez ce QR code au coach à votre arrivée pour être pointé·e présent·e.
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <h2 className="mt-10 overline">Historique</h2>
      <div className="mt-3 space-y-3">
        {past.map(b => (
          <div key={b.id} className="card-warm p-5 flex items-center justify-between opacity-70">
            <div>
              <div className="font-semibold">{b.session.title}</div>
              <div className="text-sm text-[var(--muted-text)]">{fmt(b.session.start_at)}</div>
            </div>
            <div className="text-xs">
              {b.cancelled ? <span className="text-red-600">Annulé</span> : b.attended ? <span className="text-[var(--forest)]">Présent</span> : <span>—</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MySubs() {
  const [subs, setSubs] = useState([]);
  const [carnets, setCarnets] = useState([]);
  useEffect(() => { api.get("/my/subscriptions").then(r => setSubs(r.data)); api.get("/carnets").then(r => setCarnets(r.data)); }, []);

  return (
    <div>
      <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Mes carnets</h1>
      <div className="mt-6 space-y-4">
        {subs.length === 0 && <div className="text-[var(--muted-text)]">Aucun carnet actif.</div>}
        {subs.map(s => {
          const isUnlimited = s.unlimited;
          const isExpired = s.expires_at && new Date(s.expires_at) < new Date();
          return (
          <div key={s.id} className={`card-warm p-6 ${isExpired ? "opacity-50" : ""}`} data-testid={`my-subscription-${s.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: "var(--pine)" }}>
                  {s.carnet_name}
                  {isUnlimited && <span className="badge-discipline badge-yoga">Illimité</span>}
                  {isExpired && <span className="badge-discipline badge-marche">Expiré</span>}
                </h3>
                <div className="text-sm text-[var(--muted-text)]">
                  Acheté le {new Date(s.created_at).toLocaleDateString("fr-FR")}
                  {s.expires_at && ` · Valable jusqu'au ${new Date(s.expires_at).toLocaleDateString("fr-FR")}`}
                </div>
              </div>
              <div className="text-right">
                {isUnlimited ? (
                  <div className="text-3xl font-serif-display font-semibold" style={{ color: "var(--terracotta)" }} data-testid={`subscription-unlimited-${s.id}`}>
                    ∞ <span className="text-lg text-[var(--muted-text)]">séances</span>
                  </div>
                ) : (
                  <div className="text-3xl font-serif-display font-semibold" style={{ color: "var(--terracotta)" }} data-testid={`subscription-remaining-credits-${s.id}`}>
                    {s.sessions_remaining} <span className="text-lg text-[var(--muted-text)]">/ {s.sessions_total}</span>
                  </div>
                )}
                <div className="text-xs text-[var(--muted-text)]">{isUnlimited ? "accès illimité" : "séances restantes"}</div>
              </div>
            </div>
            {!isUnlimited && (
              <div className="mt-4 grid grid-cols-10 gap-1">
                {Array.from({ length: Math.min(s.sessions_total, 50) }).map((_, i) => (
                  <div key={i} className={`h-2 rounded-full ${i < s.sessions_remaining ? "bg-[var(--forest)]" : "bg-slate-200"}`} />
                ))}
              </div>
            )}
          </div>
        );})}
      </div>

      <h2 className="mt-12 font-serif-display text-2xl font-semibold" style={{ color: "var(--pine)" }}>Acheter un carnet</h2>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {carnets.map(c => (
          <div key={c.id} className="card-warm p-6" data-testid={`buy-carnet-${c.id}`}>
            <Sparkles size={20} className="text-[var(--terracotta)]" />
            <h3 className="mt-3 font-serif-display text-2xl font-semibold flex items-center gap-2" style={{ color: "var(--pine)" }}>
              {c.name}
              {c.unlimited && <span className="badge-discipline badge-yoga">Illimité</span>}
            </h3>
            <div className="mt-2 text-4xl font-serif-display font-semibold">{c.price_eur}€</div>
            {c.unlimited ? (
              <div className="text-sm text-[var(--muted-text)] mt-1">
                Accès illimité{c.validity_days ? ` pendant ${c.validity_days} jours` : ""}
              </div>
            ) : (
              <div className="text-sm text-[var(--muted-text)] mt-1">{(c.price_eur / c.sessions_count).toFixed(2)} € / séance</div>
            )}
            {c.description && <div className="text-sm text-[var(--muted-text)] mt-2 italic">{c.description}</div>}
            <div className="mt-5 p-4 rounded-lg bg-[var(--sand)] text-sm text-[var(--muted-text)]" data-testid={`buy-carnet-info-${c.id}`}>
              <div className="font-medium text-[var(--pine)] mb-1">Paiements acceptés</div>
              Espèces · QR Code (Bancontact) · Virement bancaire<br/>
              Contactez-nous par email pour l'obtenir — nous créditerons votre compte dès réception.
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ParticipantDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };
  const links = [
    { to: "/mon-espace", label: "Séances disponibles", icon: Calendar, end: true, key: "browse" },
    { to: "/mon-espace/planning", label: "Mon planning", icon: User, key: "planning" },
    { to: "/mon-espace/carnets", label: "Mes carnets", icon: CreditCard, key: "carnets" },
  ];
  return (
    <div className="min-h-screen flex" style={{ background: "var(--sand)" }}>
      <aside className="dashboard-nav w-64 p-6 hidden md:flex flex-col">
        <div className="font-serif-display text-xl font-semibold text-white">Mon espace</div>
        <nav className="mt-8 space-y-1 flex-1">
          {links.map(l => (
            <NavLink key={l.key} to={l.to} end={l.end}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`participant-nav-${l.key}`}>
              <l.icon size={16}/> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="text-xs text-white/60 mb-2">{user?.email}</div>
          <button onClick={doLogout} className="nav-link w-full" data-testid="participant-logout"><LogOut size={16}/> Déconnexion</button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-12 py-4 border-b bg-[var(--sand)]/90 backdrop-blur flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => nav(-1)} className="btn-ghost text-sm inline-flex items-center gap-1.5 border" data-testid="participant-back-btn">
              <ArrowLeft size={16}/> Précédent
            </button>
            <button onClick={() => nav("/mon-espace")} className="btn-ghost text-sm inline-flex items-center gap-1.5 border" data-testid="participant-home-btn">
              <Home size={16}/> Accueil
            </button>
            {/* Mobile-only quick nav */}
            <div className="md:hidden flex gap-1 ml-2">
              <button onClick={() => nav("/mon-espace/planning")} className="btn-ghost text-xs border" data-testid="participant-mobile-planning">Planning</button>
              <button onClick={() => nav("/mon-espace/carnets")} className="btn-ghost text-xs border" data-testid="participant-mobile-carnets">Carnets</button>
            </div>
          </div>
          <a href="/" className="text-sm text-[var(--muted-text)] hover:text-[var(--pine)] inline-flex items-center gap-1.5" data-testid="participant-view-site">
            Site public <ExternalLink size={14}/>
          </a>
        </div>
        <div className="p-6 md:p-12">
          <Routes>
            <Route index element={<Browse />} />
            <Route path="planning" element={<MyPlanning />} />
            <Route path="carnets" element={<MySubs />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
